import { Component as ReactComponent, Dispatch, SetStateAction } from 'react'
import { toast } from 'react-toastify'

import {
  Accordion,
  AccordionSummary,
  Button,
  Dialog,
  Icon,
  Switch,
  Textarea
} from '@clientbase/clientbase-library'
import { Box, Tooltip, Typography } from '@mui/material'
import { getAPIClient } from 'api/axios'
import { filesize } from 'filesize'

import ExpirationAccordion from 'components/Modal/ModalUploadComponent/ExpirationAccordion'
import Upload from 'components/UploadComponent/Upload'

import { deleteDocumentFile } from 'utils/documentFiles'

import { theme } from 'styles/theme'

import { Container, Content, Props, State } from '.'
class UploadComponent extends ReactComponent<Props, State> {
  state: State = {
    uploadedFiles: [],
    description: '',
    submited: false,
    visibleInPortal: false,
    expirationEnabled: false,
    expirationDate: null,
    reminderDaysBefore: 7,
    confirmDeleteId: null
  }
  // Uploads em andamento: com vários arquivos na mesma soltura, o loading
  // externo só desliga quando o último termina.
  pendingUploads = 0

  finishUpload = () => {
    this.pendingUploads = Math.max(this.pendingUploads - 1, 0)
    if (this.pendingUploads === 0) {
      !!this.props.setLoadingExternal && this.props.setLoadingExternal(false)
    }
  }

  componentDidMount() {
    this.hydrateInitialFiles()
  }

  componentDidUpdate(prevProps: Props) {
    // Comparação por referência: quem passa `initialFiles` monta a lista uma vez, a partir
    // do GET do registro. Comparar conteúdo aqui só custaria varredura a cada render.
    if (prevProps.initialFiles !== this.props.initialFiles) {
      this.hydrateInitialFiles()
    }
  }

  // Semeia o estado interno com os anexos que já existiam no registro. Ver o porquê em
  // `Props.initialFiles`: sem isto o componente se comporta como se o registro não tivesse
  // anexo nenhum — não deleta no servidor e não conta para o `maxFiles`.
  hydrateInitialFiles = () => {
    const initial = this.props.initialFiles

    if (!initial?.length) return

    this.setState((prev) => ({
      uploadedFiles: [
        ...initial.filter(
          (file) => !prev.uploadedFiles.some((known) => known.id === file.id)
        ),
        ...prev.uploadedFiles
      ]
    }))
  }

  // Arquivo ja gravado no servidor: pergunta antes, porque a exclusao vale na hora
  // (nao depende do Salvar) e nao tem desfazer. Arquivo ainda nao enviado sai direto —
  // confirmar a remocao de algo que so existe na tela seria atrito a toa.
  handleDelete = (id: string) => {
    const file = this.state.uploadedFiles.find((file) => file.id === id)
    const excluiNoServidor =
      !this.props.submit && !this.props.noSuppressAuto && !!file?.uploaded

    if (!excluiNoServidor) {
      this.removeFromList(id)
      return
    }

    this.setState({ confirmDeleteId: id })
  }

  confirmDelete = async () => {
    const id = this.state.confirmDeleteId
    if (!id) return

    this.setState({ confirmDeleteId: null })

    const uuid = this?.props?.uuid ? `/${this.props.uuid}` : ''
    const excluiu = await deleteDocumentFile(
      `/v1/${this.props.type}${uuid}/documents/${id}`
    )

    // Falhou, o arquivo continua na lista: sumir da tela sem ter saido do servidor era o
    // que fazia o comprovante "voltar" no reload seguinte.
    if (excluiu) this.removeFromList(id)
  }

  removeFromList = (id: string) => {
    this.setState((prev) => ({
      uploadedFiles: prev.uploadedFiles.filter((file) => file.id !== id)
    }))

    !!this.props.setStateExternal &&
      this.props.setStateExternal((state) =>
        state.filter((file) => file.id !== id)
      )
    this.props.setUploadIsDirty && this.props.setUploadIsDirty(true)
  }

  handleUpload = (files: any) => {
    const uploadedFiles = files.map((file: any) => ({
      file,
      id: Math.floor(Math.random() * 100),
      name: file.name,
      readableSize: filesize(file.size),
      preview: URL.createObjectURL(file),
      progress: 0,
      uploaded: false,
      error: false,
      url: null
    }))
    this.props.setUploadIsDirty && this.props.setUploadIsDirty(true)
    this.setState((prev) => ({
      uploadedFiles: prev.uploadedFiles.concat(uploadedFiles)
    }))
    if (this.props.setError) {
      this.props.setError(false)
    }

    !!this.props.setStateExternal &&
      this.props.setStateExternal((state) => state.concat(uploadedFiles))

    if (!this.props.submit && !this.props.noSuppressAuto) {
      uploadedFiles.forEach((file: any) =>
        this.processUpload(file, this.props.uuid)
      )
    }
  }

  handleUploadSubmit = () => {
    const { uploadedFiles } = this.state
    if (!uploadedFiles.length) {
      this.props.setError && this.props.setError(true)
      return
    }
    uploadedFiles.forEach((file) => this.processUpload(file, this.props.uuid))
  }

  updateFile = (id: string, data: any) => {
    this.setState((prev) => ({
      uploadedFiles: prev.uploadedFiles.map((uploadedFile) => {
        return id === uploadedFile.id
          ? { ...uploadedFile, ...data }
          : uploadedFile
      })
    }))

    !!this.props.setStateExternal &&
      this.props.setStateExternal((state) =>
        state.map((uploadedFile) => {
          return id === uploadedFile.id
            ? { ...uploadedFile, ...data }
            : uploadedFile
        })
      )
  }

  processUpload = (uploadedFile: any, uuid?: string) => {
    const formData = new FormData()

    const docType = {
      rg: '1',
      cpf: '2',
      cnpj: '3',
      cnh: '4',
      residency_proof: '5',
      activity_proof: '6',
      receipt: '7',
      documet_whith_photo: '8',
      import: '9',
      contract: '10',
      announcement: '11',
      product_image: '13',
      terms: '20',
      nfse: '18',
      account_payable: '19',
      sales_page: '21',
      selfie: '22',
      cnh_full: '23',
      cnh_front: '24',
      cnh_back: '25',
      rg_front: '26',
      rg_back: '27',
      documents: '100',
      other: '100'
    }

    formData.append('document', uploadedFile.file)
    formData.append('doc_type', docType[this.props.docType])
    formData.append('description', this.state.description)
    Object.entries(this.props.extraFormData || {}).forEach(([key, value]) => {
      if (value) formData.append(key, value)
    })
    if (this.props.showVisibleInPortal) {
      formData.append('visible_in_portal', String(this.state.visibleInPortal))
    }
    if (
      this.props.showExpiration &&
      this.state.expirationEnabled &&
      this.state.expirationDate
    ) {
      const d = this.state.expirationDate
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        '0'
      )}-${String(d.getDate()).padStart(2, '0')}`
      formData.append('expiration_date', iso)
      formData.append(
        'reminder_days_before',
        String(this.state.reminderDaysBefore)
      )
    }

    const uuidReceived = uuid ? `/${uuid}` : ''
    this.pendingUploads += 1
    this.props.setLoadingExternal && this.props.setLoadingExternal(true)
    getAPIClient()
      .post(`/v1/${this.props.type}${uuidReceived}/documents`, formData, {
        onUploadProgress: (event: ProgressEvent) => {
          const { loaded, total } = event
          const progress = Math.round((loaded * 100) / total)
          const intProgress = progress.toString()

          this.updateFile(uploadedFile.id, {
            progress: intProgress
          })
        }
      })
      .then((response: any) => {
        this.updateFile(uploadedFile.id, {
          uploaded: true,
          id: response.data.record.uuid,
          url: response.data.record.doc_file_url
        })

        if (this.props.resetAfterSubmit) {
          this.setState({
            description: '',
            uploadedFiles: []
          })
          if (this.props.refreshTable) {
            this.props.refreshTable()
            if (this.props.onUploadComplete) {
              this.props.onUploadComplete()
            }
          }
        }

        this.finishUpload()
      })
      .catch((err: any) => {
        this.updateFile(uploadedFile.id, {
          error: true
        })

        const message =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          'Erro ao enviar o documento. Tente novamente.'
        toast.error(message)

        this.finishUpload()
      })
  }
  render() {
    const { uploadedFiles } = this.state
    const documentErrorMessage =
      this.props?.helperText || 'Você deve selecionar um documento.'

    return (
      <Container sx={{ gap: '8px' }}>
        <Content>
          <Upload
            onUpload={this.handleUpload}
            uploadedFilesLength={uploadedFiles.length}
            title={this.props.title}
            variant={this.props.variant || 'normal'}
            isLoading={this.props.isLoading}
            subTitle={this.props.subTitle}
            maxFiles={this.props.maxFiles}
            maxSize={this.props.maxSize}
            sentIcon={this.props.sentIcon}
            disabled={this.props.disabled}
            accept={this.props.accept}
            error={this.props.error}
            disabledText={this.props.disabledText}
            invalidTypeMessage={this.props.invalidTypeMessage}
            tooManyFilesMessage={this.props.tooManyFilesMessage}
          />

          {!this.props.disableList && !!uploadedFiles.length && (
            <Box display="flex" flexDirection="column" gap="6px">
              {uploadedFiles.map((file) => (
                <Box
                  key={file.id}
                  padding="5px"
                  display="grid"
                  gridTemplateColumns={'90% 10%'}
                >
                  <Box
                    onClick={() => {
                      if (file.url) window.open(file.url, '_blank', 'noopener')
                    }}
                    sx={{ cursor: file.url ? 'pointer' : 'default' }}
                    display="flex"
                    flexDirection="column"
                  >
                    <Typography
                      fontSize="12px"
                      color="#999"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Nome do arquivo:{' '}
                      <strong
                        style={{
                          textDecoration: file.url ? 'underline' : 'none'
                        }}
                      >
                        {file.name}
                      </strong>
                    </Typography>
                    <Typography fontSize="12px" color="#999">
                      Tamanho: {file.readableSize}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ cursor: 'pointer' }}
                    display="flex"
                    justifyContent="end"
                    alignItems="center"
                  >
                    <Icon
                      sx={{
                        color: theme.palette.neutral[400],
                        width: '18px',
                        height: '18px',
                        transform: 'rotate(45deg)'
                      }}
                      onClick={() => this.handleDelete(file.id)}
                      icon="addCircle"
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Content>
        {this.props.error && (
          <Typography mt="-5px" ml="4px" color="#CC2942" variant="xsBold">
            {documentErrorMessage}
          </Typography>
        )}
        <Box>
          {this.props.description && (
            <Textarea
              onChange={(event) => {
                this.setState({
                  description: event.target.value
                })
              }}
              value={this.state.description}
              placeholder="Descrição"
              fontSize="14px"
              style={{
                height: '80px',
                resize: 'vertical',
                maxHeight: '50vh'
              }}
            />
          )}

          {this.props.showVisibleInPortal && (
            <Accordion
              expanded={false}
              onChange={() =>
                this.setState({ visibleInPortal: !this.state.visibleInPortal })
              }
              sx={{
                mt: 2,
                border: '1px solid #E8EBE8',
                borderLeft: '3px solid #01f7c2',
                borderRadius: '8px !important',
                backgroundColor: '#FAFBFA',
                boxShadow: 'none',
                '&:before': { display: 'none' },
                '&.Mui-expanded': {
                  margin: 0
                }
              }}
            >
              <AccordionSummary
                hasSwitch
                expandIcon={
                  <Switch
                    checked={this.state.visibleInPortal}
                    value={this.state.visibleInPortal}
                    onChange={(e) =>
                      this.setState({ visibleInPortal: e.target.checked })
                    }
                    onClick={(e) => e.stopPropagation()}
                  />
                }
              >
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="smBold">
                    Visível no portal do cliente
                  </Typography>
                  <Tooltip
                    enterTouchDelay={0}
                    arrow
                    placement="top"
                    title="Se ativado, o cliente poderá visualizar este documento no portal"
                  >
                    <Box>
                      <Icon
                        icon="info"
                        sx={{
                          fontSize: '1.2rem',
                          mt: '3px',
                          color: theme.palette.neutral[700]
                        }}
                      />
                    </Box>
                  </Tooltip>
                </Box>
              </AccordionSummary>
            </Accordion>
          )}

          {this.props.showExpiration && (
            <ExpirationAccordion
              enabled={this.state.expirationEnabled}
              onToggle={(enabled) =>
                this.setState({ expirationEnabled: enabled })
              }
              expirationDate={this.state.expirationDate}
              onChangeExpirationDate={(date) =>
                this.setState({ expirationDate: date })
              }
              reminderDaysBefore={this.state.reminderDaysBefore}
              onChangeReminderDaysBefore={(days) =>
                this.setState({ reminderDaysBefore: days })
              }
            />
          )}

          {this.props.submit && (
            <Button
              sx={{ mt: '18px', fontWeight: '400' }}
              onClick={this.handleUploadSubmit}
              fullWidth={true}
            >
              Salvar
            </Button>
          )}
        </Box>

        <Dialog
          title="Confirma excluir este arquivo?"
          description={
            this.state.uploadedFiles.find(
              (file) => file.id === this.state.confirmDeleteId
            )?.name || ''
          }
          maxWidth="xs"
          icon="delete"
          isOpenDialog={!!this.state.confirmDeleteId}
          // O Dialog espera um Dispatch<SetStateAction<boolean>>, então aceita tanto o
          // valor quanto um updater. Aqui o "aberto" é derivado de confirmDeleteId:
          // fechar (Cancelar, Esc, clique fora) só precisa limpar o id.
          setIsOpenDialog={
            ((open: boolean | ((prev: boolean) => boolean)) => {
              const aberto =
                typeof open === 'function'
                  ? open(!!this.state.confirmDeleteId)
                  : open

              if (!aberto) this.setState({ confirmDeleteId: null })
            }) as Dispatch<SetStateAction<boolean>>
          }
          cancelButton
        >
          <Button fullWidth onClick={this.confirmDelete}>
            Excluir
          </Button>
        </Dialog>
      </Container>
    )
  }
}

export default UploadComponent
